import { useRef, useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────
interface OrderDetalle {
  id?: number;
  producto_nombre?: string | null;
  producto_id?: number;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
  notas?: string | null;
}
interface OrderData {
  id: number;
  numero_pedido: string;
  cliente_nombre: string;
  fecha_pedido: string | null;
  metodo_pago?: string | null;
  status: string;
  pagado: boolean;
  total: number;
  descuento: number;
  detalles?: OrderDetalle[];
}
export interface TicketModalProps {
  order: OrderData;
  onClose: () => void;
}

// ─── Canvas constants ─────────────────────────────────────────────────────────
const W       = 800;
const BG      = "#FFF8ED";
const INK     = "#111111";
const ORANGE  = "#F05A24";
const TW      = 20;
const TH      = 22;
const PAD     = 58;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T12:00:00" : iso);
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fmtMXN(n: number): string {
  return `$${Math.round(n).toLocaleString("es-MX")} MXN`;
}
function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
}
function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ─── Main drawing function ────────────────────────────────────────────────────
async function drawTicket(canvas: HTMLCanvasElement, order: OrderData) {
  // Ensure web fonts are loaded
  await document.fonts.ready;
  try { await document.fonts.load("700 14px Manrope"); } catch { /* ok */ }

  const detalles    = order.detalles ?? [];
  const hasDiscount = order.descuento > 0;

  // ── Calculate canvas height ─────────────────────────────────────────────
  const H =
    TH                                       // top zigzag
    + 30                                     // padding top
    + 155                                    // logo stamp (cy = TH+30+62+14=128, bottom = 190)
    + 68                                     // title + gap
    + 28                                     // separator
    + 4 * 56                                 // 4 data rows
    + 28                                     // separator
    + (detalles.length > 0 ? 38 + detalles.length * 50 : 0) // products
    + 28                                     // separator
    + (hasDiscount ? 2 * 50 : 0)             // subtotal + discount rows
    + 68                                     // big MONTO row
    + 28                                     // separator
    + 74                                     // footer
    + 30                                     // padding bottom
    + TH;                                    // bottom zigzag

  canvas.width  = W;
  canvas.height = H;

  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, W, H);

  // ── Ticket shape with zigzag ─────────────────────────────────────────────
  const toothCount = Math.floor(W / (TW * 2));
  const tStart     = (W - toothCount * TW * 2) / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, TH);
  ctx.lineTo(tStart, TH);
  // Top zigzag — teeth pointing UP
  for (let i = 0; i < toothCount; i++) {
    const x = tStart + i * TW * 2;
    ctx.lineTo(x + TW,       0);
    ctx.lineTo(x + TW * 2,   TH);
  }
  ctx.lineTo(W, TH);
  ctx.lineTo(W, H - TH);
  // Bottom zigzag — teeth pointing DOWN (right → left)
  ctx.lineTo(tStart + toothCount * TW * 2, H - TH);
  for (let i = toothCount - 1; i >= 0; i--) {
    const x = tStart + i * TW * 2;
    ctx.lineTo(x + TW,  H);
    ctx.lineTo(x,       H - TH);
  }
  ctx.lineTo(0, H - TH);
  ctx.closePath();
  ctx.fillStyle = BG;
  ctx.fill();
  ctx.restore();

  let y = TH + 30;
  const cx = W / 2;

  // ── Load logo ────────────────────────────────────────────────────────────
  let logo: HTMLImageElement | null = null;
  let useMult = false;
  try { logo = await loadImg("/logo_sin_fondo.png"); }
  catch { try { logo = await loadImg("/logo.png"); useMult = true; } catch { /* skip */ } }

  // ── Logo stamp (circle + rotating text) ──────────────────────────────────
  const LOGO_R = 62;
  const TEXT_R = LOGO_R + 14;
  const logocy = y + LOGO_R + 14;

  // Logo image (clipped to circle)
  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, logocy, LOGO_R, 0, Math.PI * 2);
    ctx.clip();
    if (useMult) ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(logo, cx - LOGO_R, logocy - LOGO_R, LOGO_R * 2, LOGO_R * 2);
    ctx.restore();
  }

  y = logocy + LOGO_R + 20;

  // ── Title ─────────────────────────────────────────────────────────────────
  ctx.fillStyle = ORANGE;
  ctx.font         = "800 36px Manrope, sans-serif";
  ctx.textAlign    = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("GRACIAS POR TU COMPRA", cx, y + 36);
  y += 68;

  // ── Separator helper ──────────────────────────────────────────────────────
  function sep() {
    ctx.save();
    ctx.strokeStyle = INK;
    ctx.lineWidth   = 1.5;
    ctx.setLineDash([7, 5]);
    ctx.beginPath();
    ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
    y += 28;
  }

  // ── Data row helper ───────────────────────────────────────────────────────
  function row(label: string, value: string) {
    ctx.fillStyle    = INK;
    ctx.textBaseline = "alphabetic";
    ctx.font         = "700 20px Manrope, sans-serif";
    ctx.textAlign    = "left";
    ctx.fillText(label, PAD, y + 24);
    ctx.font         = "400 20px Manrope, sans-serif";
    ctx.textAlign    = "right";
    ctx.fillText(value, W - PAD, y + 24);
    y += 56;
  }

  sep();
  row("FOLIO:", order.numero_pedido);
  row("CLIENTE:", order.cliente_nombre);
  row("FECHA:", fmtDate(order.fecha_pedido));
  row("MÉTODO DE PAGO:", (order.metodo_pago && order.metodo_pago !== "n/a"
    ? order.metodo_pago : "N/A").toUpperCase());

  sep();

  // ── Products ──────────────────────────────────────────────────────────────
  if (detalles.length > 0) {
    ctx.fillStyle = INK;
    ctx.font      = "700 15px Manrope, sans-serif";
    ctx.textAlign = "left";  ctx.fillText("PRODUCTO", PAD, y + 20);
    ctx.textAlign = "center"; ctx.fillText("CANT.", cx, y + 20);
    ctx.textAlign = "right";  ctx.fillText("SUBTOTAL", W - PAD, y + 20);
    y += 38;

    detalles.forEach((d) => {
      const name = d.producto_nombre ?? `Producto #${d.producto_id}`;
      ctx.fillStyle    = INK;
      ctx.textBaseline = "alphabetic";
      ctx.font         = "400 19px Manrope, sans-serif";
      ctx.textAlign    = "left";  ctx.fillText(name, PAD, y + 22);
      ctx.textAlign    = "center"; ctx.fillText(`×${d.cantidad}`, cx, y + 22);
      ctx.font         = "600 19px Manrope, sans-serif";
      ctx.textAlign    = "right";
      ctx.fillText(`$${Math.round(d.subtotal)}`, W - PAD, y + 22);
      y += 50;
    });
  }

  sep();

  // ── Totals ────────────────────────────────────────────────────────────────
  const subtotal = detalles.reduce((s, d) => s + d.subtotal, 0) || order.total;

  if (hasDiscount) {
    ctx.fillStyle    = INK;
    ctx.font         = "400 20px Manrope, sans-serif";
    ctx.textBaseline = "alphabetic";
    ctx.textAlign    = "left";  ctx.fillText("SUBTOTAL:", PAD, y + 24);
    ctx.textAlign    = "right"; ctx.fillText(`$${Math.round(subtotal)}`, W - PAD, y + 24);
    y += 50;
    ctx.textAlign    = "left";  ctx.fillText("DESCUENTO:", PAD, y + 24);
    ctx.textAlign    = "right"; ctx.fillText(`-$${Math.round(order.descuento)}`, W - PAD, y + 24);
    y += 50;
  }

  // MONTO — el más grande
ctx.fillStyle    = ORANGE;
ctx.textBaseline = "alphabetic";
ctx.font         = "700 21px Manrope, sans-serif";
ctx.textAlign    = "left";
ctx.fillText("MONTO:", PAD, y + 32);

ctx.font      = "800 48px Manrope, sans-serif";
ctx.textAlign = "right";
ctx.fillText(fmtMXN(order.total), W - PAD, y + 36);

y += 68;

  sep();

  // ── Footer ────────────────────────────────────────────────────────────────
  const fy    = y + 14;
  const ICON  = 30;
  const iconX = cx - 108;

  // Instagram icon (rounded square)
  ctx.strokeStyle = INK;
  ctx.lineWidth   = 2.5;
  rrect(ctx, iconX, fy, ICON, ICON, 7); ctx.stroke();
  ctx.beginPath();
  ctx.arc(iconX + ICON/2, fy + ICON/2, ICON/2 - 7, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = INK;
  ctx.beginPath();
  ctx.arc(iconX + ICON - 8, fy + 7, 2.8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle    = INK;
  ctx.font         = "600 22px Manrope, sans-serif";
  ctx.textAlign    = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("@cold.crew.coffee", iconX + ICON + 14, fy + ICON / 2);
}

// ─── Component ────────────────────────────────────────────────────────────────
export function TicketModal({ order, onClose }: TicketModalProps) {
  const canvasRef              = useRef<HTMLCanvasElement>(null);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    if (!canvasRef.current) return;
    setRendering(true);
    drawTicket(canvasRef.current, order)
      .then(() => setRendering(false))
      .catch(() => setRendering(false));
  }, [order]);

  function handleDownload() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `coldcrew_ticket_${order.numero_pedido}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, "image/png");
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md my-8">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
        >
          <X className="size-6" />
        </button>

        {/* Ticket canvas */}
        <div
  className="relative"
  style={{
    filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.28))",
  }}
>
          {rendering && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: BG }}
            >
              <Loader2 className="size-8 animate-spin" style={{ color: INK }} />
            </div>
          )}
          <canvas ref={canvasRef} className="w-full block" />
        </div>

        {/* Download button */}
        {!rendering && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-colors hover:opacity-90 active:scale-[0.98]"
              style={{ background: "#090909" }}
            >
              <Download className="size-4" />
              Descargar ticket
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
